import React, { memo, useEffect, useState, useRef } from 'react';
import { FaSpinner, FaClock } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';

interface ViolinPlotProgressDisplayProps {
    isGeneratingDatasets: boolean;
    analysisProgress: number;
    analysisCurrentStep: string;
    eta?: number | null;
}

const formatEta = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Progress display for dataset/violin plot generation.
 * For model training progress use RealTimeTrainingProgress.
 */
const ViolinPlotProgressDisplay: React.FC<ViolinPlotProgressDisplayProps> = memo(({
    isGeneratingDatasets,
    analysisProgress,
    analysisCurrentStep,
    eta: _eta
}) => {
    const { t } = useTranslation('training');

    const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
    const lastStepRef = useRef<string>('');

    useEffect(() => {
        if (analysisCurrentStep !== lastStepRef.current) {
            setElapsedSeconds(0);
            lastStepRef.current = analysisCurrentStep;
        }
    }, [analysisCurrentStep]);

    useEffect(() => {
        if (!isGeneratingDatasets) {
            setElapsedSeconds(0);
            return;
        }
        const interval = setInterval(() => {
            setElapsedSeconds(prev => prev + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [isGeneratingDatasets]);

    useEffect(() => {
        if (isGeneratingDatasets) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isGeneratingDatasets]);

    if (!isGeneratingDatasets) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 backdrop-blur-[6px]" />

            {/* Card */}
            <div className="relative z-10 w-full max-w-sm sm:max-w-md mx-2 sm:mx-4 rounded-xl border border-border/80 bg-card shadow-sm overflow-hidden animate-slideUp">
                {/* Header */}
                <div className="px-5 pt-5 pb-0 sm:px-6 sm:pt-6">
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                                <FaSpinner className="animate-spin text-primary text-sm" />
                            </div>
                            <span className="text-xs sm:text-sm font-semibold text-foreground tracking-tight truncate">
                                {t('training.notifications.progress.generatingDatasets')}
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                            {elapsedSeconds > 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground font-mono tabular-nums">
                                    <FaClock className="text-[8px]" />
                                    {formatEta(elapsedSeconds)}
                                </span>
                            )}
                            <span className="text-xs sm:text-sm font-semibold text-primary tabular-nums">
                                {analysisProgress}%
                            </span>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-accent rounded-full h-2 overflow-hidden">
                        <div
                            className="bg-primary h-2 rounded-full transition-all duration-500 ease-out relative overflow-hidden"
                            style={{ width: `${analysisProgress}%` }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-progress-shine bg-[length:200%_100%]" />
                        </div>
                    </div>
                </div>

                {/* Step + Message */}
                <div className="px-5 py-4 sm:px-6 sm:py-4">
                    {analysisCurrentStep && (
                        <div className="flex items-center gap-2 py-2 px-3 bg-muted/80 rounded-lg border border-border mb-2.5">
                            <FaSpinner className="animate-spin text-muted-foreground text-[10px] flex-shrink-0" />
                            <p className="text-[11px] sm:text-xs text-muted-foreground">
                                <span className="font-medium text-foreground">{t('training.notifications.progress.currentStep')}:</span>{' '}
                                {t(analysisCurrentStep)}
                            </p>
                        </div>
                    )}

                    <p className="text-[10px] sm:text-[11px] text-muted-foreground leading-relaxed">
                        {t('training.notifications.progress.pleaseWaitDatasets')}
                    </p>
                </div>
            </div>
        </div>
    );
});

ViolinPlotProgressDisplay.displayName = 'ViolinPlotProgressDisplay';

export default ViolinPlotProgressDisplay;
